import { API_URL, WS_URL } from "config";

export type BotsManagementWsEvent = {
  event?: string;
  task?: {
    task_id?: number;
    user_id?: number;
    connection_id?: number;
    job_id?: number | null;
    task_status?: string | null;
    bot_work_status?: string | null;
  };
  job?: {
    job_id?: number;
  };
};

type EventListener = (event: BotsManagementWsEvent) => void;
type StatusListener = (connected: boolean) => void;
type AuthRejectedListener = () => void;
type TokenProvider = () => string | null;

const WS_CLOSE_UNAUTHORIZED = 4401;
const WS_CLOSE_FORBIDDEN = 4403;
const HEARTBEAT_INTERVAL_MS = 15000;
// An open socket is not the same as a working one: a stale server (or a channel
// layer that cannot reach this process) leaves it open and silent. The pong the
// server sends back to each ping is the only proof the round trip still works,
// so consumers are told "disconnected" when it stops arriving - that is what
// their polling fallback is for.
const HEARTBEAT_TIMEOUT_MS = HEARTBEAT_INTERVAL_MS * 3;

class BotsManagementWsManager {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectDelayMs = 2000;
  private disposed = false;
  private authRejected = false;
  private connected = false;
  private lastPongAt = 0;
  private token: string | null = null;
  private getToken: TokenProvider | null = null;
  private eventListeners = new Set<EventListener>();
  private statusListeners = new Set<StatusListener>();
  private authRejectedListeners = new Set<AuthRejectedListener>();

  subscribe(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  subscribeAuthRejected(listener: AuthRejectedListener): () => void {
    this.authRejectedListeners.add(listener);
    return () => this.authRejectedListeners.delete(listener);
  }

  setTokenProvider(provider: TokenProvider | null) {
    this.getToken = provider;
  }

  isConnected(): boolean {
    return this.connected;
  }

  connect(token: string) {
    if (!token) return;
    if (this.token === token && this.socket?.readyState === WebSocket.OPEN) {
      return;
    }
    this.token = token;
    this.disposed = false;
    this.authRejected = false;
    this.teardownSocket();
    this.openSocket();
  }

  disconnect() {
    this.disposed = true;
    this.authRejected = false;
    this.token = null;
    this.teardownSocket();
    this.setConnected(false);
  }

  private resolveToken(): string | null {
    const freshToken = this.getToken?.() ?? null;
    return freshToken || this.token;
  }

  private notifyEvent(event: BotsManagementWsEvent) {
    this.eventListeners.forEach((listener) => listener(event));
  }

  private notifyAuthRejected() {
    this.authRejectedListeners.forEach((listener) => listener());
  }

  private setConnected(next: boolean) {
    if (this.connected === next) return;
    this.connected = next;
    this.statusListeners.forEach((listener) => listener(next));
  }

  private teardownSocket() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
      this.socket = null;
    }
  }

  private scheduleReconnect() {
    if (this.disposed || this.authRejected) return;
    this.reconnectTimer = setTimeout(() => this.openSocket(), this.reconnectDelayMs);
    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 30000);
  }

  private openSocket() {
    if (this.disposed || this.authRejected) return;

    const token = this.resolveToken();
    if (!token) return;

    this.token = token;
    const wsBaseRaw = WS_URL || API_URL.replace(/^http:\/\//, "ws://").replace(/^https:\/\//, "wss://");
    const wsBase = wsBaseRaw.replace(/\/+$/, "");
    const wsUrl = `${wsBase}/ws/bots-management/?token=${encodeURIComponent(token)}`;
    this.socket = new WebSocket(wsUrl);
    this.socket.onopen = () => {
      this.lastPongAt = Date.now();
      this.setConnected(true);
      this.reconnectDelayMs = 2000;
      this.heartbeatTimer = setInterval(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) return;
        if (Date.now() - this.lastPongAt > HEARTBEAT_TIMEOUT_MS) {
          // Silent socket: report it as down so the UI falls back to polling,
          // and start over rather than sitting on a connection that is mute.
          this.setConnected(false);
          this.socket.close();
          return;
        }
        this.socket.send(JSON.stringify({ type: "ping" }));
      }, HEARTBEAT_INTERVAL_MS);
    };
    this.socket.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data) as
          | BotsManagementWsEvent
          | { type?: string };
        if ((payload as { type?: string })?.type === "pong") {
          this.lastPongAt = Date.now();
          this.setConnected(true);
          return;
        }
        const event = payload as BotsManagementWsEvent;
        if (event?.event || event?.task || event?.job) {
          // Traffic is proof of life too, not just the heartbeat.
          this.lastPongAt = Date.now();
          this.notifyEvent(event);
        }
      } catch {
        return;
      }
    };
    this.socket.onerror = () => {
      this.socket?.close();
    };
    this.socket.onclose = (event) => {
      this.setConnected(false);
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      if (this.disposed) return;

      if (event.code === WS_CLOSE_UNAUTHORIZED || event.code === WS_CLOSE_FORBIDDEN) {
        this.authRejected = true;
        if (event.code === WS_CLOSE_UNAUTHORIZED) {
          this.notifyAuthRejected();
        }
        return;
      }

      this.scheduleReconnect();
    };
  }
}

export const botsManagementWs = new BotsManagementWsManager();
